import { Link } from 'react-router-dom';
import {
  MPC_ID,
  CORE_PRINCIPLES,
  ARCHITECTURAL_PRINCIPLES,
  CANONICAL_ENTITIES,
  TENANT_SCOPES,
  EVENT_FIELDS,
  EVENT_CATEGORIES,
  AI_REQUIREMENTS,
  DNA_LAYERS,
  PROMPT_GOVERNANCE_LAYERS,
  SECURITY_PRINCIPLES,
  ACCESSIBILITY_STANDARDS,
  DESIGN_PRINCIPLES,
  OBSERVABILITY_SIGNALS,
  QUALITY_VALIDATIONS,
  RELEASE_GATES,
  DATABASE_PRINCIPLES,
  API_PRINCIPLES,
  TESTING_TYPES,
  DEPLOYMENT_PRINCIPLES,
  DEFINITION_OF_DONE,
  NON_NEGOTIABLE_RULES,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_KPIS,
  DEMO_ARCHITECTURE,
  DEMO_TENANT_EVENT,
  DEMO_AI_GOVERNANCE,
  DEMO_SECURITY_EXPERIENCE,
  DEMO_QUALITY,
  DEMO_STANDARDS,
  DEMO_GOVERNANCE,
  buildConstitutionDashboard,
} from '../../masterplatform/framework';
import {
  ConstitutionOverviewPanel,
  ArchitectureKernelPanel,
  TenantEventPanel,
  AIGovernancePanel,
  SecurityExperiencePanel,
  QualityObservabilityPanel,
  PlatformStandardsPanel,
  GovernanceCompliancePanel,
} from '../../components/masterplatform';
import { Card, KpiCard } from '../../components/ui';

export function MasterPlatformConstitutionPage() {
  const dashboard = buildConstitutionDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{MPC_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Master Platform Constitution</h1>
        <p className="mt-1 text-sm text-slate-500">
          Supreme governing authority of the AI Commerce Operating System — every architectural, implementation,
          AI, deployment, and business decision SHALL comply with this constitution.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-900 dark:bg-indigo-950 dark:text-indigo-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard label="Core Principles" value={dashboard.core_principles.toString()} />
        <KpiCard label="Architectural Principles" value={dashboard.architectural_principles.toString()} />
        <KpiCard label="Canonical Entities" value={dashboard.canonical_entities.toString()} />
        <KpiCard label="Release Gates" value={dashboard.release_gates.toString()} />
        <KpiCard label="Non-Negotiable Rules" value={dashboard.non_negotiable_rules.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Constitution Architecture</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Constitution Overview</h2>
        <p className="mt-1 text-sm text-slate-500">{CORE_PRINCIPLES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ConstitutionOverviewPanel kpis={DEMO_KPIS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Architecture & Canonical Commerce Kernel</h2>
        <p className="mt-1 text-sm text-slate-500">{ARCHITECTURAL_PRINCIPLES.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{CANONICAL_ENTITIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ArchitectureKernelPanel items={DEMO_ARCHITECTURE} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Multi-Tenant & Event Model</h2>
        <p className="mt-1 text-sm text-slate-500">{TENANT_SCOPES.join(' · ')} · {EVENT_CATEGORIES.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{EVENT_FIELDS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <TenantEventPanel items={DEMO_TENANT_EVENT} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Constitution & DNA Governance</h2>
        <p className="mt-1 text-sm text-slate-500">{AI_REQUIREMENTS.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{DNA_LAYERS.join(' · ')} · {PROMPT_GOVERNANCE_LAYERS.join(' → ')}</p>
        <Card className="mt-4" padding="sm">
          <AIGovernancePanel items={DEMO_AI_GOVERNANCE} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Security, Accessibility & Design System</h2>
        <p className="mt-1 text-sm text-slate-500">{SECURITY_PRINCIPLES.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{ACCESSIBILITY_STANDARDS.join(' · ')} · {DESIGN_PRINCIPLES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <SecurityExperiencePanel items={DEMO_SECURITY_EXPERIENCE} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Quality, Observability & Release Governance</h2>
        <p className="mt-1 text-sm text-slate-500">{QUALITY_VALIDATIONS.join(' · ')} · {RELEASE_GATES.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{OBSERVABILITY_SIGNALS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <QualityObservabilityPanel items={DEMO_QUALITY} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Platform Standards</h2>
        <p className="mt-1 text-sm text-slate-500">
          {DATABASE_PRINCIPLES.join(' · ')} · {API_PRINCIPLES.join(' · ')} · {TESTING_TYPES.join(' · ')} · {DEPLOYMENT_PRINCIPLES.join(' · ')}
        </p>
        <Card className="mt-4" padding="sm">
          <PlatformStandardsPanel items={DEMO_STANDARDS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Governance & Non-Negotiable Rules</h2>
        <p className="mt-1 text-sm text-slate-500">{NON_NEGOTIABLE_RULES.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">Definition of Done: {DEFINITION_OF_DONE.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <GovernanceCompliancePanel items={DEMO_GOVERNANCE} />
        </Card>
      </section>
    </div>
  );
}
